import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../../app/layouts/dashboard';
import VerifiedApprovedGuard from '../../app/guards/VerifiedApprovedGuard';
import LogoOnlyLayout from '../../app/layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../../app/guards/GuestGuard';
import AuthGuard from '../../app/guards/AuthGuard';
import { PATH_AFTER_LOGIN } from '../../app/config';
// components
import { LoadingScreen } from '../components/base';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: '',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'sign-up',
          element: (
            <GuestGuard>
              <SignUp />
            </GuestGuard>
          ),
        },

        { path: 'login-unprotected', element: <Login /> },
        { path: 'reset-password', element: <ResetPassword /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <Dashboard /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Dashboard />, index: true },
        { path: 'account', element: <AccountSettings /> },
        { path: 'restaurant/create', element: (
          <VerifiedApprovedGuard>
            <RestaurantCreate />
          </VerifiedApprovedGuard>
        ) },
        { path: 'tour/create', element: (
          <VerifiedApprovedGuard>
            <TourCreate />
          </VerifiedApprovedGuard>
        ) },
        { path: 'restaurant-listings', element: <RestaurantListings /> },
        { path: 'tour-listings', element: <TourListings /> },
        { path: 'restaurant/:id', element: <RestaurantDetails /> },
        { path: 'tour/:id', element: <TourDetails /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../../modules/auth/Login')));
const SignUp = Loadable(lazy(() => import('../../modules/auth/SignUp')));
const ResetPassword = Loadable(lazy(() => import('../../modules/auth/ResetPassword')));

// GENERAL
const Dashboard = Loadable(lazy(() => import('../../modules/dashboard/Dashboard2/Dashboard2')));
const AccountSettings = Loadable(lazy(() => import('../../modules/account/Account/Account')));
const RestaurantCreate = Loadable(lazy(() => import('../../modules/restaurants/RestaurantCreate/RestaurantCreate')));
const RestaurantListings = Loadable(lazy(() => import('../../modules/listings/RestaurantListings/RestaurantListings')));
const TourListings = Loadable(lazy(() => import('../../modules/listings/TourListings/TourListings')));
const RestaurantDetails = Loadable(lazy(() => import('../../modules/listings/RestaurantDetails/RestaurantDetails')));
const TourDetails = Loadable(lazy(() => import('../../modules/listings/TourDetails/TourDetails')));
const TourCreate = Loadable(lazy(() => import('../../modules/activities/ActivityCreate/ActivityCreate')));

// MAIN
const Page500 = Loadable(lazy(() => import('../../modules/auth/Page500')));
const Page403 = Loadable(lazy(() => import('../../modules/auth/Page403')));
const Page404 = Loadable(lazy(() => import('../../modules/auth/Page404')));
